import { useHistory } from 'react-router-dom'

import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import Styles from '../../ContextMenus.module.scss'
import { HandbookItem, HandbookItemType, FileType, AlmanacFile } from 'types'
import { ActionHandler, ActionKey, MenuItem } from '../../Types'
import { ReactComponent as CopyIcon } from 'assets/icons/copy16.svg'
import { ReactComponent as BigCopyIcon } from 'assets/icons/copy24.svg'
import { useHandbookItems } from 'components/Handbook2_0/Sidebar/HandbookItemProvider'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { CopyModal, Item } from 'components/CopyModal/CopyModal'
import { useDefaultProject } from 'hooks/queries/projects/useDefaultProject'
import { SuccessfulCopyCallBack } from 'components/CopyModal/components/CopyActions/CopyActions'
import { useCurrentFolderStore } from 'hooks/stores/useCurrentFolderStore'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { getFolderPath } from 'utils/urlUtils'
import { FolderClient } from 'api/clients/FolderClient'
import { useFinderRefresh } from 'screens/Finder/hooks/useFinderRefresh'
import { handbookItemLink, stringType } from 'components/Handbook2_0/util'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { reportError } from 'utils/error'
import { useRootStore } from 'stores/RootStore'
import { useModals } from 'almanac-ui/Modal'

const createCopyable = (label: string = '') => {
  return [{ key: ActionKey.copy, textValue: label, Icon: CopyIcon }]
}

interface Copyable {
  copyable: MenuItem[]
  handleCopy: ActionHandler
  openCopyModal: ActionHandler
}

export const useCopyable = (item: HandbookItem, actionLabel: string = ''): Copyable => {
  const { currentHandbook } = useHandbookItems()
  const handbooksStore = useRootStore().handbooks
  const refreshFinder = useFinderRefresh()
  const workspaceId = useCurrentWorkspaceId()
  const defaultProjectQuery = useDefaultProject(workspaceId)
  const defaultProject = defaultProjectQuery?.data?.project || null
  const history = useHistory()
  const { openModal, closeModal } = useModals()
  const { currentFolder } = useCurrentFolderStore()
  const treeItem = handbooksStore.items.get(item.id.toString())

  const handleDuplication = () => {
    if (!treeItem) {
      return
    }
    const hideLoading = showToastMessage(
      <span className={Styles.loadingMessage}>
        <BigCopyIcon aria-hidden="true" /> Copying the {HandbookItemType.Document === item.type ? 'document' : 'folder'}{' '}
        "{item.title}"...
      </span>,
      'loading',
      undefined,
      0,
    )
    treeItem
      .duplicate()
      .then((dupItem) => {
        hideLoading()
        if (!!currentHandbook && [HandbookItemType.Document, HandbookItemType.NestedHandbook].includes(dupItem.type)) {
          history.push(handbookItemLink(currentHandbook, dupItem))
        }
      })
      .catch((res) => {
        showErrorMessageToast(`There was a problem copying the ${stringType(item.type)}. Please try again.`)
        reportError('Error duplicating handbook item', { metaData: { res } })
      })
  }

  const handleCopy = (key: ActionKey) => {
    if (key !== ActionKey.copy || !treeItem) return false

    if ([HandbookItemType.Document, HandbookItemType.NestedHandbook].includes(item.type)) handleDuplication()
    else
      treeItem.duplicate().catch((res) => {
        showErrorMessageToast(`There was a problem copying the ${stringType(item.type)}. Please try again.`)
        reportError('Error duplicating handbook item', { metaData: { res } })
      })

    return true
  }

  const onItemCopied: SuccessfulCopyCallBack = (copiedItem, destination) => {
    closeModal()

    if (!workspaceId || !currentFolder) return

    handbooksStore.refresh(destination.selectedFolder?.handbook_key)
    refreshFinder()

    if (destination?.selectedFolder?.id === currentFolder.id) {
      showToastMessage(`You just copied ${item.title}`, 'success')
      return
    } else {
      showToastMessage(
        `You just copied ${item.title} to ${destination?.selectedFolder?.title || 'Folders'}`,
        'success',
        {
          onPress: () => {
            // TODO: Fix this the next time the file is edited.
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const folderSlug = destination.selectedFolder?.seo_string || copiedItem.handbook!.seo_string
            if (!folderSlug) return

            const path = getFolderPath({ folderSlug })
            history.push(path)
          },
          variant: 'proceed',
          label: 'Go to folder',
          type: 'button',
        },
        5,
      )
    }
  }

  const openCopyModal = (key: ActionKey) => {
    if (key !== ActionKey.copy) return false

    let article: Item | undefined = undefined
    let itemType: FileType | undefined = undefined

    if (item.document) {
      article = {
        id: item.document.key,
        header_emoji: item.document.header_emoji,
        title: item.title || '',
        linked_copies_count: item.document.linked_copy_count || 0,
      }
      itemType = 'article'
    }

    if (!article) return false

    const openNewModal = (folder: AlmanacFile | null) => {
      if (!article) return

      openModal(CopyModal, {
        item: article,
        onItemCopied,
        folder,
        itemType,
      })
    }

    const loadFolder = async () => {
      const folder = treeItem?.parentHandbook?.handbook?.folder
      if (!folder) return defaultProject

      const response = await FolderClient.getFolder(folder.key)

      if (response.type === SUCCESS_RESPONSE_TYPE) {
        const { folder } = response.data

        return folder
      }

      return null
    }

    loadFolder().then((folder) => {
      openNewModal(folder)
    })

    return true
  }

  return {
    copyable: createCopyable(actionLabel),
    handleCopy,
    openCopyModal,
  }
}
